import { type FC, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Flex,
  HStack,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { IoFilter } from 'react-icons/io5';

import { IconCalendarOutlineGray, IconStatistics } from '@/components';
import { FiltersHeader } from '@/components/Filters/Header';
import { StatusFilterComponent } from '@/components/Filters/Status/StatusFilterComponent';
import { PopoverDayPicker, PopoverItemRow } from '@/components/Popover';
import { Status } from '@/services/api';

export type StatusItemsObj = Record<Status, boolean>;

type AdminInterviewsFiltersProps = {
  status: StatusItemsObj;
  setStatus: (value: StatusItemsObj) => void;
  dateRangeSearchTerm: DateRange;
  setDateRangeSearchTerm: (value: DateRange) => void;
  clearAllFilters: () => void;
  setPage: (page: number) => void;
};

export const statusFilterItems: Array<{
  name: Status;
  text: string;
}> = [
  {
    name: Status.Active,
    text: t('components:status.active'),
  },
  {
    name: Status.Inactive,
    text: t('components:status.inactive'),
  },
];

export const AdminInterviewsFilters: FC<AdminInterviewsFiltersProps> = ({
  dateRangeSearchTerm,
  setDateRangeSearchTerm,
  status,
  setStatus,
  clearAllFilters,
  setPage,
}) => {
  const { t } = useTranslation(['common', 'components']);
  const [activeFilterType, setActiveFilterType] = useState<FilterTypes>();
  const [dateRange, setDateRange] = useState<DateRange>(dateRangeSearchTerm);
  const parentPopover = useDisclosure();
  const childPopover = useDisclosure();

  const handleCloseAllPopovers = () => {
    parentPopover.onClose();
    childPopover.onClose();
  };

  const handlePopoverSwitch = (filterType: FilterTypes) => {
    setActiveFilterType(filterType);
    if (!childPopover.isOpen) {
      childPopover.onOpen();
    }
  };

  const adminInterviewsFilterItems = [
    {
      key: 'Date',
      name: t('components:dashboard.filters.date'),
      icon: <IconCalendarOutlineGray fontSize="20px" mr={2} />,
      marked:
        Boolean(dateRangeSearchTerm.from) && Boolean(dateRangeSearchTerm.to),
    },
    {
      key: 'Status',
      name: t('components:dashboard.filters.status'),
      icon: <IconStatistics fontSize="20px" mr={2} />,
      marked: Object.values(status).some(Boolean),
    },
  ] as const;

  type FilterTypes = (typeof adminInterviewsFilterItems)[number]['key'];

  const filtersAppliedN = useMemo(() => {
    const appliedChecks = [
      Boolean(dateRangeSearchTerm.from) && Boolean(dateRangeSearchTerm.to),
      Object.values(status).some(Boolean),
    ];

    return appliedChecks.filter(Boolean).length;
  }, [dateRangeSearchTerm, status]);

  const onSubmitDateRange = (dateRange: DateRange) => {
    setPage(1);
    setDateRangeSearchTerm(dateRange);
    handleCloseAllPopovers();
  };

  return (
    <HStack>
      <Popover
        closeOnEsc
        closeOnBlur
        placement="bottom-start"
        returnFocusOnClose={false}
        isOpen={parentPopover.isOpen}
        onClose={handleCloseAllPopovers}
      >
        <PopoverAnchor>
          <Button
            size="md"
            leftIcon={<IoFilter />}
            rightIcon={
              filtersAppliedN ? (
                <Text color="brand.500" fontSize="md" fontWeight="semibold">
                  ({filtersAppliedN})
                </Text>
              ) : undefined
            }
            boxShadow={parentPopover.isOpen ? '0 0 0 4px #F0E9F8' : 'none'}
            onClick={
              parentPopover.isOpen
                ? handleCloseAllPopovers
                : parentPopover.onOpen
            }
          >
            {t('common:actions.filter')}
          </Button>
        </PopoverAnchor>
        <Portal>
          <PopoverContent
            w="200px"
            borderRadius="xl"
            borderColor="gray.100"
            shadow="md"
          >
            <PopoverBody p={0}>
              <Popover
                closeOnBlur
                placement="right-start"
                isOpen={childPopover.isOpen}
                onClose={childPopover.onClose}
              >
                <PopoverAnchor>
                  <Flex direction="column">
                    <FiltersHeader
                      filtersAppliedN={filtersAppliedN}
                      onClear={() => {
                        clearAllFilters();
                        handleCloseAllPopovers();
                      }}
                      onClose={handleCloseAllPopovers}
                    />
                    {adminInterviewsFilterItems.map((item) => {
                      return (
                        <PopoverItemRow
                          key={item.key}
                          name={item.name}
                          icon={item.icon}
                          onRowClick={() => handlePopoverSwitch(item.key)}
                          marked={item.marked}
                        />
                      );
                    })}
                  </Flex>
                </PopoverAnchor>

                <PopoverContent shadow="md">
                  {activeFilterType === 'Date' ? (
                    <Box pr={2}>
                      <PopoverDayPicker
                        wrappedInPopoverContent={false}
                        canDeselectSelectedDate
                        dayPickerMode="range"
                        dateRangeValue={dateRange}
                        setDateRangeValue={(rangeDate) =>
                          setDateRange(rangeDate!)
                        }
                        onSubmit={(rangeDate) => onSubmitDateRange(rangeDate!)}
                        isFilter
                      />
                    </Box>
                  ) : activeFilterType === 'Status' ? (
                    <StatusFilterComponent
                      initialValues={status}
                      statusFilterItems={statusFilterItems}
                      onSubmit={(newData) => {
                        setPage(1);
                        setStatus(newData);
                      }}
                      onclose={handleCloseAllPopovers}
                    />
                  ) : null}
                </PopoverContent>
              </Popover>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </HStack>
  );
};
